import React, { useEffect, useMemo, useState } from "react";
import { IoFlagSharp } from "react-icons/io5";
import {
  calculateJJCombatResult,
  getJJPoints,
} from "../../utils/jjCombatScoring";

const getCompetiteurDisplayName = (competiteur, fallback = "Combattant") => {
  if (!competiteur) return fallback;
  const nom = competiteur.nom_competiteur || competiteur.nom || "";
  const prenom = competiteur.prenom_competiteur || competiteur.prenom || "";
  const full = `${nom} ${prenom}`.trim();
  return full || fallback;
};

const normalizeDisciplineMode = (discipline = "judo") => {
  const value = String(discipline || "").trim().toLowerCase();

  if (
    [
      "jj_combat",
      "ju-jitsu combat",
      "jujitsu combat",
      "jj combat",
      "ju-jitsu",
      "jujitsu",
      "jujitsu fighting",
      "ju-jitsu fighting",
    ].includes(value)
  ) {
    return "jj_combat";
  }

  return "judo";
};

const DetermineWinnerModal = ({
  isVisible,
  onClose,
  score1,
  score2,
  competiteur1,
  competiteur2,
  onValidate,
  penalites1,
  penalites2,
  discipline = "judo",
  autoResult = null,
  chrono = 0,
  onRequestExtraPeriod,
}) => {
  const [hanteiWinner, setHanteiWinner] = useState(0);

  const disciplineMode = useMemo(
    () => normalizeDisciplineMode(discipline),
    [discipline],
  );

  // ------------------------------
  // JUDO
  // ------------------------------
  const decideJudo = () => {
    if (score1?.forfait) return { winner: 2, reason: "Forfait" };
    if (score2?.forfait) return { winner: 1, reason: "Forfait" };
    if (score1?.hansokuMake) return { winner: 2, reason: "Hansoku Make" };
    if (score2?.hansokuMake) return { winner: 1, reason: "Hansoku Make" };

    const i1 = +score1?.ippon || 0;
    const i2 = +score2?.ippon || 0;
    if (i1 !== i2) return { winner: i1 > i2 ? 1 : 2, reason: null };

    const w1 = +score1?.wazari || 0;
    const w2 = +score2?.wazari || 0;
    if (w1 !== w2) return { winner: w1 > w2 ? 1 : 2, reason: null };

    const y1 = +score1?.yuko || 0;
    const y2 = +score2?.yuko || 0;
    if (y1 !== y2) return { winner: y1 > y2 ? 1 : 2, reason: null };

    return { winner: 0, reason: "Égalité" };
  };

  const baseDecisionJudo = useMemo(() => decideJudo(), [score1, score2]);
  const isTieJudo = baseDecisionJudo.reason === "Égalité";

  const effectiveWinnerJudo =
    baseDecisionJudo.winner === 0 && hanteiWinner
      ? hanteiWinner
      : baseDecisionJudo.winner;

  const effectiveReasonJudo =
    isTieJudo && hanteiWinner ? "Hantei" : baseDecisionJudo.reason;

  // ------------------------------
  // JJ COMBAT
  // ------------------------------
  const jjResult = useMemo(() => {
    if (disciplineMode !== "jj_combat") return null;
    if (autoResult) return autoResult;
    return calculateJJCombatResult(score1 || {}, score2 || {});
  }, [disciplineMode, autoResult, score1, score2]);

  const jjPoints1 = useMemo(() => {
    if (disciplineMode !== "jj_combat") return null;
    return getJJPoints(score1 || {});
  }, [disciplineMode, score1]);

  const jjPoints2 = useMemo(() => {
    if (disciplineMode !== "jj_combat") return null;
    return getJJPoints(score2 || {});
  }, [disciplineMode, score2]);

  const baseDecisionJJ = useMemo(() => {
    if (disciplineMode !== "jj_combat") {
      return { winner: 0, reason: null };
    }

    if (score1?.forfait) return { winner: 2, reason: "Forfait" };
    if (score2?.forfait) return { winner: 1, reason: "Forfait" };
    if (score1?.hansokuMake) return { winner: 2, reason: "Hansoku Make" };
    if (score2?.hansokuMake) return { winner: 1, reason: "Hansoku Make" };

    if (jjResult?.result1 === "V" && jjResult?.result2 === "D") {
      return { winner: 1, reason: "Victoire aux points" };
    }

    if (jjResult?.result1 === "D" && jjResult?.result2 === "V") {
      return { winner: 2, reason: "Victoire aux points" };
    }

    if (jjResult?.needExtraPeriod) {
      return { winner: 0, reason: "Période supplémentaire" };
    }

    return { winner: 0, reason: "Égalité" };
  }, [disciplineMode, score1, score2, jjResult]);

  const effectiveWinner =
    disciplineMode === "jj_combat" ? baseDecisionJJ.winner : effectiveWinnerJudo;

  const effectiveReason =
    disciplineMode === "jj_combat" ? baseDecisionJJ.reason : effectiveReasonJudo;

  const isTieForUI =
    disciplineMode === "jj_combat"
      ? effectiveWinner === 0
      : isTieJudo;

  useEffect(() => {
    if (isVisible) setHanteiWinner(0);
  }, [isVisible]);

  if (!isVisible) return null;

  const name1 = getCompetiteurDisplayName(competiteur1, "Rouge");
  const name2 = getCompetiteurDisplayName(
    competiteur2,
    disciplineMode === "jj_combat" ? "Bleu" : "Blanc",
  );

  const showReason =
    effectiveReason === "Forfait" ||
    effectiveReason === "Hansoku Make" ||
    effectiveReason === "Égalité" ||
    effectiveReason === "Hantei" ||
    effectiveReason === "Période supplémentaire" ||
    effectiveReason === "Victoire aux points";

  const reasonTone = (() => {
    switch (effectiveReason) {
      case "Forfait":
      case "Hansoku Make":
        return "bg-rose-500/15 text-rose-500 border-rose-400/40";
      case "Égalité":
        return "bg-amber-500/15 text-amber-600 border-amber-400/40";
      case "Hantei":
        return "bg-emerald-500/15 text-emerald-700 border-emerald-400/40 dark:text-emerald-300";
      case "Période supplémentaire":
        return "bg-yellow-400/15 text-yellow-300 border-yellow-300/30";
      case "Victoire aux points":
        return "bg-blue-500/15 text-blue-600 border-blue-400/40 dark:text-blue-300";
      default:
        return "hidden";
    }
  })();

  const winnerText =
    effectiveWinner === 1
      ? `${name1} gagne`
      : effectiveWinner === 2
        ? `${name2} gagne`
        : disciplineMode === "jj_combat" && effectiveReason === "Période supplémentaire"
          ? "Période supplémentaire requise"
          : "Égalité";

  const cardClasses = (who) => {
    const isWinner = effectiveWinner === who;
    const isLoser = effectiveWinner !== 0 && effectiveWinner !== who;

    if (who === 1) {
      if (isWinner) {
        return "bg-rose-500/10 border-rose-500/30 dark:bg-rose-400/10 dark:border-rose-300/30";
      }
      if (isLoser) return "bg-rose-500/5 border-rose-500/15 opacity-80";
      return "bg-rose-500/5 border-rose-500/20";
    }

    if (disciplineMode === "jj_combat") {
      if (isWinner) {
        return "bg-blue-500/10 border-blue-500/30 dark:bg-blue-400/10 dark:border-blue-300/30";
      }
      if (isLoser) return "bg-blue-500/5 border-blue-500/15 opacity-80";
      return "bg-blue-500/5 border-blue-500/20";
    }

    if (isWinner) {
      return "bg-slate-200/70 border-slate-300/60 dark:bg-slate-700/40 dark:border-slate-600/40";
    }
    if (isLoser) {
      return "bg-slate-200/50 border-slate-300/40 dark:bg-slate-800/40 dark:border-slate-700/40 opacity-80";
    }
    return "bg-slate-200/60 border-slate-300/50 dark:bg-slate-700/30 dark:border-slate-600/30";
  };

  const flagBadge = (who) => {
    const active = effectiveWinner === who;
    if (!active) return null;

    if (who === 1) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-extrabold bg-rose-600 text-white">
          <IoFlagSharp className="text-base" />
          VAINQUEUR
        </span>
      );
    }

    if (disciplineMode === "jj_combat") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-extrabold bg-blue-600 text-white">
          <IoFlagSharp className="text-base" />
          VAINQUEUR
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-extrabold bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900">
        <IoFlagSharp className="text-base" />
        VAINQUEUR
      </span>
    );
  };

  const buildDetailsCombatJJ = () => {
    const pts1 = getJJPoints(score1 || {});
    const pts2 = getJJPoints(score2 || {});
    const result = jjResult || calculateJJCombatResult(score1 || {}, score2 || {});

    const redName = name1;
    const blueName = name2;

    let winnerName = "À déterminer";
    let decisionLabel = "Égalité";

    if (result?.result1 === "V" && result?.result2 === "D") {
      winnerName = redName;
      decisionLabel = "Victoire aux points";
    } else if (result?.result1 === "D" && result?.result2 === "V") {
      winnerName = blueName;
      decisionLabel = "Victoire aux points";
    } else if (score1?.forfait) {
      winnerName = blueName;
      decisionLabel = "Victoire par forfait";
    } else if (score2?.forfait) {
      winnerName = redName;
      decisionLabel = "Victoire par forfait";
    } else if (score1?.hansokuMake) {
      winnerName = blueName;
      decisionLabel = "Victoire par Hansoku Make";
    } else if (score2?.hansokuMake) {
      winnerName = redName;
      decisionLabel = "Victoire par Hansoku Make";
    } else if (result?.needExtraPeriod) {
      decisionLabel = "Période supplémentaire";
    }

    return [
      `Gagnant : ${winnerName}`,
      `Décision : ${decisionLabel}`,
      `Rouge : total ${pts1.total} pts | P1 ${pts1.part1} | P2 ${pts1.part2} | P3 ${pts1.part3} | Ippon P1 ${score1?.part1Ippon || 0} | Ippon P2 ${score1?.part2Ippon || 0} | Ippon P3 ${score1?.part3Ippon || 0} | Shido ${score1?.shido || 0} | Chui ${score1?.chui || 0}${score1?.hansokuMake ? " | Hansoku Make" : ""}${score1?.forfait ? " | Forfait" : ""}`,
      `Bleu : total ${pts2.total} pts | P1 ${pts2.part1} | P2 ${pts2.part2} | P3 ${pts2.part3} | Ippon P1 ${score2?.part1Ippon || 0} | Ippon P2 ${score2?.part2Ippon || 0} | Ippon P3 ${score2?.part3Ippon || 0} | Shido ${score2?.shido || 0} | Chui ${score2?.chui || 0}${score2?.hansokuMake ? " | Hansoku Make" : ""}${score2?.forfait ? " | Forfait" : ""}`,
    ].join(" • ");
  };

  const handleValidation = () => {
    if (disciplineMode === "jj_combat") {
      const result = jjResult || calculateJJCombatResult(score1 || {}, score2 || {});

      if (result?.needExtraPeriod) {
        return;
      }

      const payload = {
        result1: result?.result1 || "N",
        result2: result?.result2 || "N",
        points1: result?.points1 || 0,
        points2: result?.points2 || 0,
        details_combat: buildDetailsCombatJJ(),
        penalites1: Number(score1?.shido || 0),
        penalites2: Number(score2?.shido || 0),
        forfait1: Boolean(score1?.forfait),
        forfait2: Boolean(score2?.forfait),
        hansoku1: Boolean(score1?.hansokuMake),
        hansoku2: Boolean(score2?.hansokuMake),
        meta: {
          discipline: "jj_combat",
          total1: jjPoints1?.total || 0,
          total2: jjPoints2?.total || 0,
          part1_1: jjPoints1?.part1 || 0,
          part2_1: jjPoints1?.part2 || 0,
          part3_1: jjPoints1?.part3 || 0,
          part1_2: jjPoints2?.part1 || 0,
          part2_2: jjPoints2?.part2 || 0,
          part3_2: jjPoints2?.part3 || 0,
          shido1: Number(score1?.shido || 0),
          shido2: Number(score2?.shido || 0),
          chui1: Number(score1?.chui || 0),
          chui2: Number(score2?.chui || 0),
        },
      };

      onValidate(payload);
      onClose();
      return;
    }

    // ------------------------------
    // Validation Judo (inchangée)
    // ------------------------------
    let result1 = "N";
    let result2 = "N";

    const i1 = +score1?.ippon || 0;
    const w1 = +score1?.wazari || 0;
    const y1 = +score1?.yuko || 0;
    const i2 = +score2?.ippon || 0;
    const w2 = +score2?.wazari || 0;
    const y2 = +score2?.yuko || 0;

    let points1 = i1 * 10 + w1 * 7 + y1 * 5;
    let points2 = i2 * 10 + w2 * 7 + y2 * 5;

    const shido1 = Number.isFinite(+penalites1)
      ? +penalites1
      : Number.isFinite(+score1?.penalites)
        ? +score1.penalites
        : 0;

    const shido2 = Number.isFinite(+penalites2)
      ? +penalites2
      : Number.isFinite(+score2?.penalites)
        ? +score2.penalites
        : 0;

    let decisionMode = "POINTS";

    if (score1?.forfait) {
      result1 = "F";
      result2 = "V";
      points1 = 0;
      points2 = 10;
      decisionMode = "FORFAIT";
    } else if (score2?.forfait) {
      result2 = "F";
      result1 = "V";
      points1 = 10;
      points2 = 0;
      decisionMode = "FORFAIT";
    } else if (score1?.hansokuMake) {
      result1 = "H";
      result2 = "V";
      points1 = 0;
      points2 = 10;
      decisionMode = "HANSOKU";
    } else if (score2?.hansokuMake) {
      result2 = "H";
      result1 = "V";
      points1 = 10;
      points2 = 0;
      decisionMode = "HANSOKU";
    } else {
      if (i1 !== i2) {
        result1 = i1 > i2 ? "V" : "D";
        result2 = i1 > i2 ? "D" : "V";
      } else if (w1 !== w2) {
        result1 = w1 > w2 ? "V" : "D";
        result2 = w1 > w2 ? "D" : "V";
      } else if (y1 !== y2) {
        result1 = y1 > y2 ? "V" : "D";
        result2 = y1 > y2 ? "D" : "V";
      } else {
        decisionMode = "EGALITE";

        if (hanteiWinner === 1) {
          result1 = "V";
          result2 = "D";
          decisionMode = "HANTEI";
        } else if (hanteiWinner === 2) {
          result1 = "D";
          result2 = "V";
          decisionMode = "HANTEI";
        }
      }
    }

    const meta = {
      ippon1: i1,
      wazari1: w1,
      yuko1: y1,
      penalites1: shido1,
      ippon2: i2,
      wazari2: w2,
      yuko2: y2,
      penalites2: shido2,
      decision: decisionMode,
      hanteiWinner: decisionMode === "HANTEI" ? hanteiWinner : null,
    };

    const n1 = name1;
    const n2 = name2;

    const block1 = `${meta.ippon1} Ippon — ${meta.wazari1} Waza-ari — ${meta.yuko1} Yuko — ${meta.penalites1} Shido`;
    const block2 = `${meta.ippon2} Ippon — ${meta.wazari2} Waza-ari — ${meta.yuko2} Yuko — ${meta.penalites2} Shido`;

    const winnerName =
      result1 === "V" || result2 === "D" || result2 === "F" || result2 === "H"
        ? n1
        : result2 === "V" || result1 === "D" || result1 === "F" || result1 === "H"
          ? n2
          : null;

    const decisionLabel =
      decisionMode === "FORFAIT"
        ? "Victoire par forfait"
        : decisionMode === "HANSOKU"
          ? "Victoire par hansoku-make"
          : decisionMode === "HANTEI"
            ? "Victoire par décision (Hantei)"
            : decisionMode === "EGALITE"
              ? "Égalité"
              : "Victoire aux points";

    const details_combat = winnerName
      ? `Gagnant : ${winnerName} • ${decisionLabel} | ${n1} — ${block1} (${points1} pts) / ${n2} — ${block2} (${points2} pts)`
      : `Égalité • ${n1} — ${block1} (${points1} pts) / ${n2} — ${block2} (${points2} pts)`;

    const payload = {
      result1,
      result2,
      points1,
      points2,
      details_combat,
      meta,
      penalites1: shido1,
      penalites2: shido2,
    };

    onValidate(payload);
    onClose();
  };

  const hanteiBtnClass = (who) => {
    const active = hanteiWinner === who;

    if (who === 1) {
      return [
        "w-full px-4 py-2.5 rounded-xl font-semibold border transition flex items-center justify-center gap-2",
        active
          ? "bg-rose-600 text-white border-rose-500 ring-2 ring-rose-500/30"
          : "bg-white dark:bg-slate-900 text-rose-700 dark:text-rose-300 border-rose-200/70 dark:border-rose-400/30 hover:bg-rose-50 dark:hover:bg-rose-500/10",
      ].join(" ");
    }

    return [
      "w-full px-4 py-2.5 rounded-xl font-semibold border transition flex items-center justify-center gap-2",
      active
        ? "bg-slate-900 text-white border-slate-700 ring-2 ring-slate-500/30 dark:bg-slate-200 dark:text-slate-900"
        : "bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border-slate-200/70 dark:border-slate-600/40 hover:bg-slate-50 dark:hover:bg-slate-800/50",
    ].join(" ");
  };

  const secondLabelIcon = disciplineMode === "jj_combat" ? "🔵" : "⚪";
  const secondNameColor =
    disciplineMode === "jj_combat"
      ? "text-blue-700 dark:text-blue-300"
      : "text-slate-700 dark:text-slate-200";

  const canValidateJJ = !jjResult?.needExtraPeriod;
  const canShowExtraPeriodButton =
    disciplineMode === "jj_combat" &&
    jjResult?.needExtraPeriod &&
    Number(chrono) <= 0 &&
    typeof onRequestExtraPeriod === "function";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full sm:max-w-2xl bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl border border-black/10 dark:border-white/10 shadow-2xl overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-black/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
          <h2 className="text-lg sm:text-2xl font-extrabold text-slate-900 dark:text-white">
            🏆 Résultat du combat
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
            {name1} <span className="opacity-60">vs</span> {name2}
          </p>
        </div>

        <div className="px-4 sm:px-6 py-5">
          <div className="text-center">
            <div
              className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs sm:text-sm font-semibold border ${reasonTone}`}
            >
              {showReason ? effectiveReason : null}
            </div>

            <p className="mt-3 sm:mt-4 text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
              {winnerText}
            </p>
          </div>

          {disciplineMode === "judo" && isTieJudo && (
            <div className="mt-5 rounded-xl border border-black/10 dark:border-white/10 bg-slate-50 dark:bg-slate-800/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-extrabold text-slate-900 dark:text-white">
                    Décision Hantei
                  </p>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    Sélectionne le vainqueur après consultation des arbitres.
                  </p>
                </div>

                {hanteiWinner !== 0 && (
                  <button
                    onClick={() => setHanteiWinner(0)}
                    className="text-xs font-semibold underline text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                    type="button"
                  >
                    Annuler
                  </button>
                )}
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setHanteiWinner(1)}
                  className={hanteiBtnClass(1)}
                >
                  <IoFlagSharp className="text-lg" />
                  Hantei Rouge
                </button>

                <button
                  type="button"
                  onClick={() => setHanteiWinner(2)}
                  className={hanteiBtnClass(2)}
                >
                  <IoFlagSharp className="text-lg" />
                  Hantei Blanc
                </button>
              </div>

              <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
                La validation enregistrera <b>V/D</b> selon le choix, sans modifier les points.
              </p>
            </div>
          )}

          {disciplineMode === "jj_combat" && jjResult?.needExtraPeriod && (
            <div className="mt-5 rounded-xl border border-yellow-300/30 bg-yellow-400/10 p-4">
              <p className="text-sm font-extrabold text-yellow-300">
                Période supplémentaire nécessaire
              </p>
              <p className="mt-1 text-xs text-yellow-100/80">
                Le combat ne peut pas être validé tant que l’égalité parfaite n’est pas départagée.
              </p>

              {canShowExtraPeriodButton && (
                <button
                  type="button"
                  onClick={() => {
                    onRequestExtraPeriod();
                    onClose();
                  }}
                  className="mt-3 px-4 py-2 rounded-xl bg-yellow-400 text-slate-950 font-bold hover:bg-yellow-300"
                >
                  Lancer la période supplémentaire
                </button>
              )}
            </div>
          )}

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={`rounded-xl p-4 border ${cardClasses(1)}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs sm:text-sm font-semibold mb-1 text-rose-700 dark:text-rose-300">
                  🔴 {name1}
                </p>
                {flagBadge(1)}
              </div>

              {disciplineMode === "jj_combat" ? (
                <>
                  <div className="text-base sm:text-lg text-slate-900 dark:text-slate-100">
                    {jjPoints1?.total || 0} pts
                  </div>
                  <div className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                    P1 {jjPoints1?.part1 || 0} — P2 {jjPoints1?.part2 || 0} — P3 {jjPoints1?.part3 || 0}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-900 text-gray-100 border border-gray-700/40">
                      {score1?.shido || 0} Shido
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-900 text-gray-100 border border-gray-700/40">
                      {score1?.chui || 0} Chui
                    </span>
                    {score1?.hansokuMake && (
                      <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-red-700 text-white">
                        Hansoku Make
                      </span>
                    )}
                    {score1?.forfait && (
                      <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-amber-600 text-white">
                        Forfait
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-base sm:text-lg text-slate-900 dark:text-slate-100">
                    {+score1?.ippon || 0} Ippon — {+score1?.wazari || 0} Waza-ari — {+score1?.yuko || 0} Yuko
                  </div>

                  {(Number.isFinite(+penalites1)
                    ? +penalites1
                    : +score1?.penalites || 0) > 0 && (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-gray-900 text-gray-100 border border-gray-700/40">
                      {(Number.isFinite(+penalites1)
                        ? +penalites1
                        : +score1?.penalites || 0)}{" "}
                      Shido
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={`rounded-xl p-4 border ${cardClasses(2)}`}>
              <div className="flex items-start justify-between gap-3">
                <p className={`text-xs sm:text-sm font-semibold mb-1 ${secondNameColor}`}>
                  {secondLabelIcon} {name2}
                </p>
                {flagBadge(2)}
              </div>

              {disciplineMode === "jj_combat" ? (
                <>
                  <div className="text-base sm:text-lg text-slate-900 dark:text-slate-100">
                    {jjPoints2?.total || 0} pts
                  </div>
                  <div className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                    P1 {jjPoints2?.part1 || 0} — P2 {jjPoints2?.part2 || 0} — P3 {jjPoints2?.part3 || 0}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-900 text-gray-100 border border-gray-700/40">
                      {score2?.shido || 0} Shido
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-slate-900 text-gray-100 border border-gray-700/40">
                      {score2?.chui || 0} Chui
                    </span>
                    {score2?.hansokuMake && (
                      <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-red-700 text-white">
                        Hansoku Make
                      </span>
                    )}
                    {score2?.forfait && (
                      <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-amber-600 text-white">
                        Forfait
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-base sm:text-lg text-slate-900 dark:text-slate-100">
                    {+score2?.ippon || 0} Ippon — {+score2?.wazari || 0} Waza-ari — {+score2?.yuko || 0} Yuko
                  </div>

                  {(Number.isFinite(+penalites2)
                    ? +penalites2
                    : +score2?.penalites || 0) > 0 && (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold bg-gray-900 text-gray-100 border border-gray-700/40">
                      {(Number.isFinite(+penalites2)
                        ? +penalites2
                        : +score2?.penalites || 0)}{" "}
                      Shido
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white font-semibold"
              type="button"
            >
              Fermer
            </button>

            <button
              onClick={handleValidation}
              disabled={disciplineMode === "jj_combat" && !canValidateJJ}
              className={`px-5 py-2.5 rounded-xl font-semibold ${
                disciplineMode === "jj_combat" && !canValidateJJ
                  ? "bg-emerald-300/40 text-white/70 cursor-not-allowed"
                  : "bg-emerald-600 hover:bg-emerald-500 text-white"
              }`}
              type="button"
            >
              {disciplineMode === "judo"
                ? isTieJudo
                  ? hanteiWinner
                    ? "Valider la décision Hantei"
                    : "Valider le match nul"
                  : "Valider le résultat"
                : canValidateJJ
                  ? "Valider le résultat"
                  : "Égalité à départager"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetermineWinnerModal;